<template>
  <div class="flex h-9 min-w-0 min-h-0 items-center gap-2 overflow-hidden" :title="`${label} · ${clickCount} clics`">
    <NuxtLink to="/app/liens" class="max-w-[7rem] truncate text-[10px] font-medium text-[#141414] hover:underline">
      {{ label }}
    </NuxtLink>
    <div class="flex items-center gap-1 rounded-md bg-[#f6f3ee] p-0.5 text-[8px] uppercase tracking-[0.08em] text-[#8a847a]">
      <button
        v-for="option in RANGE_OPTIONS"
        :key="option.value"
        type="button"
        class="rounded px-1 py-0.5 transition"
        :class="range === option.value ? 'bg-white text-[#141414] shadow-sm' : 'hover:text-[#141414]'"
        :aria-label="`${option.label} pour ${label}`"
        :aria-pressed="range === option.value"
        @click="range = option.value"
      >
        {{ option.label }}
      </button>
    </div>
    <div class="h-9 min-h-0 w-32 shrink-0 overflow-hidden">
      <AtelierChart :option="chartOption" :aria-label="`Évolution des clics de ${label}`" />
    </div>
    <div class="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[8px] leading-tight text-[#8a847a]">
      <span><strong class="font-medium text-[#141414]">Auj.</strong> {{ dayCounts.today }}</span>
      <span><strong class="font-medium text-[#141414]">Hier</strong> {{ dayCounts.yesterday }}</span>
    </div>
    <span class="whitespace-nowrap text-[9px] text-[#8a847a]">{{ clickCount }}</span>
  </div>
</template>

<script setup lang="ts">
import type { EChartsCoreOption } from 'echarts/core'
import { ATELIER_INK } from '~/utils/atelierChartPalette'

type RangeValue = '24h' | '7d' | '30d'

const props = defineProps<{
  clickCount: number
  history: Array<{ bucket: string, clicks: number }>
  label: string
}>()

const RANGE_OPTIONS: Array<{ value: RangeValue, label: string }> = [
  { value: '24h', label: 'j' },
  { value: '7d', label: '7j' },
  { value: '30d', label: '30j' }
]

const range = ref<RangeValue>('7d')
const HOUR = 3_600_000
const DAY = 86_400_000

const points = computed(() => {
  const now = Date.now()
  const isHourly = range.value === '24h'
  const count = isHourly ? 24 : range.value === '7d' ? 7 : 30
  const step = isHourly ? HOUR : DAY
  const end = isHourly ? Math.floor(now / HOUR) * HOUR : new Date().setHours(0, 0, 0, 0)
  return Array.from({ length: count }, (_, index) => {
    const start = end - (count - index - 1) * step
    const clicks = props.history.reduce((total, row) => {
      const at = new Date(row.bucket).getTime()
      return at >= start && at < start + step ? total + Number(row.clicks || 0) : total
    }, 0)
    const date = new Date(start)
    const label = isHourly
      ? date.toLocaleTimeString('fr-FR', { hour: '2-digit' })
      : date.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' })
    return { start, clicks, label, weekend: date.getDay() === 0 || date.getDay() === 6 }
  })
})

const dayCounts = computed(() => {
  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0)
  const yesterdayStart = new Date(todayStart)
  yesterdayStart.setDate(yesterdayStart.getDate() - 1)
  const today = todayStart.getTime()
  const yesterday = yesterdayStart.getTime()
  const countForDay = (start: number) => props.history.reduce((total, row) => {
    const at = new Date(row.bucket).getTime()
    return at >= start && at < start + DAY ? total + Number(row.clicks || 0) : total
  }, 0)
  return { today: countForDay(today), yesterday: countForDay(yesterday) }
})

const chartOption = computed<EChartsCoreOption>(() => ({
  animation: false,
  grid: { left: 0, right: 0, top: 2, bottom: 17 },
  xAxis: {
    type: 'category',
    data: points.value.map(point => point.label),
    axisLine: { lineStyle: { color: '#d9d2c5' } },
    axisTick: { show: false },
      axisLabel: {
        color: '#8a847a',
        fontSize: 7,
        interval: range.value === '30d' ? 4 : 0,
        hideOverlap: true,
        formatter: (value: string) => value.split(' ')[1] || value
      },
    splitLine: { show: true, lineStyle: { color: '#e4ded4', width: 1 } }
  },
  yAxis: { type: 'value', show: false, min: 0 },
  tooltip: {
    trigger: 'axis',
    confine: false,
    appendToBody: true,
    renderMode: 'html',
    backgroundColor: '#141414',
    borderWidth: 0,
    textStyle: { color: '#f6f3ee', fontSize: 11 },
    formatter: (params: any) => {
      const point = Array.isArray(params) ? params[0] : params
      return `${point?.axisValue || ''}<br/><strong>${point?.data || 0} clic${point?.data > 1 ? 's' : ''}</strong>`
    }
  },
  series: [{
    type: 'line',
    data: points.value.map(point => point.clicks),
    smooth: true,
    symbol: 'none',
    lineStyle: { width: 1.5, color: ATELIER_INK.primary },
    areaStyle: { color: ATELIER_INK.primary, opacity: 0.1 },
    markArea: {
      silent: true,
      itemStyle: { color: 'rgba(217, 210, 197, 0.28)' },
      data: points.value.filter(point => point.weekend).map(point => [
        { xAxis: point.label },
        { xAxis: point.label }
      ])
    }
  }]
}))
</script>
